import React, { useState, useEffect } from 'react';
import {
    Box,
    Card,
    CardContent,
    Typography,
    Button,
    useTheme,
    alpha,
    Skeleton
} from '@mui/material';
import {
    Lightbulb,
    ArrowForward,
    School
} from '@mui/icons-material';
import { getRandomTip } from '../../api/tips';
import { Link } from 'react-router-dom';

const HomeTipSection = () => {
    const theme = useTheme();
    const [tip, setTip] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        fetchRandomTip();
    }, []);

    const fetchRandomTip = async () => {
        try {
            const response = await getRandomTip();
            setTip(response.data);
        } catch (err) {
            console.error('Error fetching random tip:', err);
        } finally {
            setLoading(false);
        }
    };

    if (loading) {
        return (
            <Box sx={{ py: 6 }}>
                <Card
                    sx={{
                        background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.1)}, ${alpha(theme.palette.secondary.main, 0.1)})`,
                        border: `2px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                        borderRadius: 4
                    }}
                >
                    <CardContent sx={{ p: 4 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                            <Skeleton variant="circular" width={32} height={32} sx={{ mr: 2 }} />
                            <Skeleton variant="text" width={200} height={32} />
                        </Box>
                        <Skeleton variant="text" width="100%" height={24} />
                        <Skeleton variant="text" width="80%" height={24} sx={{ mb: 3 }} />
                        <Skeleton variant="rectangular" width={150} height={36} sx={{ borderRadius: 2 }} />
                    </CardContent>
                </Card>
            </Box>
        );
    }

    if (!tip) {
        return null;
    }

    return (
        <Box sx={{ py: { xs: 3, sm: 4, md: 6 } }}>
            <Card
                sx={{
                    background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.1)}, ${alpha(theme.palette.secondary.main, 0.1)})`,
                    border: `2px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                    borderRadius: { xs: 2, sm: 3, md: 4 },
                    transition: 'all 0.3s ease',
                    mx: { xs: 1, sm: 0 },
                    '&:hover': {
                        transform: { xs: 'translateY(-2px)', sm: 'translateY(-4px)' },
                        boxShadow: theme.shadows[8]
                    }
                }}
            >
                <CardContent sx={{
                    p: { xs: 2, sm: 3, md: 4 }
                }}>
                    <Box sx={{
                        display: 'flex',
                        alignItems: { xs: 'flex-start', sm: 'center' },
                        flexDirection: { xs: 'column', sm: 'row' },
                        mb: { xs: 2, sm: 2.5, md: 3 },
                        gap: { xs: 1.5, sm: 0 }
                    }}>
                        <Box
                            sx={{
                                background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                                borderRadius: '50%',
                                width: { xs: 40, sm: 44, md: 48 },
                                height: { xs: 40, sm: 44, md: 48 },
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                mr: { xs: 0, sm: 2 },
                                flexShrink: 0
                            }}
                        >
                            <Lightbulb sx={{
                                color: 'white',
                                fontSize: { xs: 20, sm: 22, md: 24 }
                            }} />
                        </Box>
                        <Box sx={{ textAlign: { xs: 'center', sm: 'left' } }}>
                            <Typography
                                variant="h5"
                                sx={{
                                    fontWeight: 700,
                                    color: theme.palette.primary.main,
                                    mb: 0.5,
                                    fontSize: {
                                        xs: '1.25rem',
                                        sm: '1.4rem',
                                        md: '1.5rem'
                                    }
                                }}
                            >
                                💡 Did You Know?
                            </Typography>
                            <Typography
                                variant="body2"
                                color="text.secondary"
                                sx={{
                                    fontSize: { xs: '0.8rem', sm: '0.875rem' }
                                }}
                            >
                                Learn something new about waste management
                            </Typography>
                        </Box>
                    </Box>

                    {tip.title && (
                        <Typography
                            variant="h6"
                            sx={{
                                mb: { xs: 1.5, sm: 2 },
                                fontWeight: 600,
                                color: theme.palette.text.primary,
                                fontSize: {
                                    xs: '1.1rem',
                                    sm: '1.2rem',
                                    md: '1.25rem'
                                },
                                lineHeight: { xs: 1.3, sm: 1.4 }
                            }}
                        >
                            {tip.title}
                        </Typography>
                    )}

                    <Typography
                        variant="body1"
                        color="text.secondary"
                        sx={{
                            mb: { xs: 2.5, sm: 3 },
                            lineHeight: { xs: 1.5, sm: 1.6 },
                            fontSize: {
                                xs: '0.9rem',
                                sm: '1rem',
                                md: '1.1rem'
                            }
                        }}
                    >
                        {tip.tipText.length > 200
                            ? `${tip.tipText.substring(0, 200)}...`
                            : tip.tipText
                        }
                    </Typography>

                    <Box sx={{
                        display: 'flex',
                        flexDirection: { xs: 'column', sm: 'row' },
                        gap: { xs: 1.5, sm: 2 },
                        alignItems: { xs: 'stretch', sm: 'center' }
                    }}>
                        <Button
                            component={Link}
                            to="/tips"
                            variant="contained"
                            endIcon={<School />}
                            sx={{
                                borderRadius: 3,
                                px: { xs: 2, sm: 3 },
                                py: { xs: 1, sm: 1.5 },
                                fontSize: { xs: '0.875rem', sm: '1rem' },
                                background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
                                '&:hover': {
                                    background: `linear-gradient(45deg, ${theme.palette.primary.dark}, ${theme.palette.primary.main})`,
                                }
                            }}
                        >
                            Learn More Tips
                        </Button>

                        <Button
                            onClick={fetchRandomTip}
                            variant="outlined"
                            endIcon={<ArrowForward />}
                            sx={{
                                borderRadius: 3,
                                px: { xs: 2, sm: 3 },
                                py: { xs: 1, sm: 1.5 },
                                fontSize: { xs: '0.875rem', sm: '1rem' },
                                color: theme.palette.primary.main,
                                borderColor: theme.palette.primary.main,
                                '&:hover': {
                                    borderColor: theme.palette.primary.dark,
                                    background: alpha(theme.palette.primary.main, 0.05)
                                }
                            }}
                        >
                            Another Tip
                        </Button>
                    </Box>
                </CardContent>
            </Card>
        </Box>
    );
};

export default HomeTipSection;
